import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { OnFieldChanged } from '../../../../../../shared/modules/configurable-form/models/on-field-changed';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { select, Store } from '@ngrx/store';
import {
  LoadPumpCapacityAction,
  LoadUnloadTypeCatalogAction,
  LoadUnloadTypeFilteredCompleteAction,
  LoadUnloadTypePlantCatalogAction,
} from '../../../actions/single-catalogs.actions';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { getOpenPlatformProducts } from '../../../selectors/products.selectors';
import { makeReference } from '@ordersnproducts/utils/mix-reference';
import { getCountry } from '@cemex/cmx-client-layout';
import { COUNTRY_CODE } from '@cemex-core/cmx-api';
import { getUnloadTypes } from '../../../selectors/single-catalogs';
import { UnloadTypePumpCodesFrance } from '@ordersnproducts/modules/shared/models/pump-type.enum';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';

@Injectable()
export class LoadSingleCatalogsUnloadType implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    this.store
      .pipe(
        select(getOpenPlatformProducts),
        filter((products) => !!value && !!products.length),
        take(1),
        map((products) =>
          products.find((product) => _safe(() => product.productCode.replace(/^0+/, '') === value, false)),
        ),
      )
      .subscribe((product) => {
        this.store.dispatch(new LoadUnloadTypeCatalogAction());
      });
  }
}

@Injectable()
export class LoadSingleCatalogsUnloadTypeMixRef implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}
  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    this.store
      .pipe(
        select(getOpenPlatformProducts),
        withLatestFrom(this.store.select(getCountry)),
        filter(([products, country]) => !!value && !!products.length),
        take(1),
        map(([products, country]) => {
          return { product: products.find((product) => makeReference(product) === value), country };
        }),
      )
      .subscribe((data) => {
        const technicalProductCode = _safe(() => data.product.technicalProducts[0].technicalProductCode);
        if (technicalProductCode) {
          if (data.country.trim() === COUNTRY_CODE.FRANCE) {
            formGroup.controls[SpecFieldNames.SLUMP].setValue(undefined, { emitEvent: false });
            formGroup.controls[SpecFieldNames.PUMPABILITY].setValue(undefined, { emitEvent: false });
            formGroup.controls[SpecFieldNames.UNLOAD_TYPE].setValue(undefined, { emitEvent: false });
            this.store.dispatch(new LoadUnloadTypePlantCatalogAction());
          } else {
            this.store.dispatch(new LoadUnloadTypeCatalogAction());
          }
        }
      });
  }
}

/**
 * Triggers update action for filtered unload types
 * Used for FR
 */
@Injectable()
export class LoadSingleCatalogsUnloadTypePlant implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    if (!!value) {
      return this.store.dispatch(new LoadUnloadTypePlantCatalogAction());
    }
  }
}

/**
 * Triggers update action for filtered unload types
 * Used for FR
 */
@Injectable()
export class LoadFilteredUnloadTypeMixRef implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}
  onFieldChanged(value, formGroup: UntypedFormGroup): void {
    this.store.pipe(select(getUnloadTypes), take(1)).subscribe((fullUnloadTypes) => {
      if (value === undefined) {
        // PRODUCT LIST PUMPABLE OR NOT -> CHECKED ON LOADER SERVICE
        this.store.dispatch(new LoadUnloadTypeFilteredCompleteAction(fullUnloadTypes));
      } else {
        const pumpCodes: string[] = Object.keys(UnloadTypePumpCodesFrance).map(
          (code) => UnloadTypePumpCodesFrance[code],
        );
        const notPumpableOptions = fullUnloadTypes.filter((option) => !pumpCodes.includes(option.unloadTypeCode));
        const filteredUnloadTypes = value === 0 ? notPumpableOptions : fullUnloadTypes;
        formGroup.controls[SpecFieldNames.UNLOAD_TYPE].setValue(undefined);
        this.store.dispatch(new LoadUnloadTypeFilteredCompleteAction(filteredUnloadTypes));
      }
    });
  }
}

@Injectable()
export class LoadPumpCapacitiesCatalogs implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(): void {
    this.store.dispatch(new LoadPumpCapacityAction());
  }
}
